/**
 * Account Property Schema - Single Source of Truth
 *
 * This composable provides centralized property name constants and type-safe
 * accessors for account data. By using this schema, property name changes
 * only require updates in ONE place instead of 10+ files.
 *
 * @example
 * ```typescript
 * const { PROPERTY_KEYS, getAccountId, setAccountId } = useAccountSchema()
 *
 * // Use constant instead of hardcoded string
 * const id = properties[PROPERTY_KEYS.ID]
 *
 * // Use accessor for type safety
 * const accountId = getAccountId(marker.properties)
 * ```
 */
export const useAccountSchema = () => {
	/**
	 * Property name constants - SINGLE SOURCE OF TRUTH
	 *
	 * When you need to rename a property, change it here ONCE.
	 * All code using these constants will automatically use the new name.
	 */
	const PROPERTY_KEYS = {
		ID: 'accountId',
		STATUS: 'accountStatus',
		ZONE_ID: 'zoneId',
		SURVEY_STATUS: 'siteSurveyStatus',
		INSIDE_INSTALL_STATUS: 'insideInstallStatus',
		OUTSIDE_INSTALL_STATUS: 'outsideInstallStatus',
		CABLE_PULLED: 'cablePulled',
		CABLE_SPLICED: 'cableSpliced',
		CABLE_TERMINATED: 'cableTerminated',
		TEMPLINE_STATUS: 'templineStatus',
		HAS_EQUIPMENT: 'hasEquipment',
		HAS_TAGS: 'hasTags'
	} as const

	// ============================================================================
	// GETTERS - Type-safe property accessors
	// ============================================================================

	/**
	 * Get account ID from any object
	 * @param obj - Object containing account properties
	 * @returns Account ID or undefined if not found
	 */
	const getAccountId = (obj: any): number | undefined => {
		const value = obj?.[PROPERTY_KEYS.ID]
		return typeof value === 'number' ? value : undefined
	}

	/**
	 * Get account status from any object
	 * @param obj - Object containing account properties
	 * @returns Account status string or undefined if not found
	 */
	const getAccountStatus = (obj: any): string | undefined => {
		const value = obj?.[PROPERTY_KEYS.STATUS]
		return typeof value === 'string' ? value : undefined
	}

	/**
	 * Get zone ID from any object
	 * @param obj - Object containing account properties
	 * @returns Zone ID or undefined if not found
	 */
	const getZoneId = (obj: any): number | undefined => {
		const value = obj?.[PROPERTY_KEYS.ZONE_ID]
		return typeof value === 'number' ? value : undefined
	}

	/**
	 * Get survey status from any object
	 * @param obj - Object containing account properties
	 * @returns Survey status string or undefined if not found
	 */
	const getSurveyStatus = (obj: any): string | undefined => {
		const value = obj?.[PROPERTY_KEYS.SURVEY_STATUS]
		return typeof value === 'string' ? value : undefined
	}

	/**
	 * Get inside install status from any object
	 * @param obj - Object containing account properties
	 * @returns Inside install status string or undefined if not found
	 */
	const getInsideInstallStatus = (obj: any): string | undefined => {
		const value = obj?.[PROPERTY_KEYS.INSIDE_INSTALL_STATUS]
		return typeof value === 'string' ? value : undefined
	}

	/**
	 * Get outside install status from any object
	 * @param obj - Object containing account properties
	 * @returns Outside install status string or undefined if not found
	 */
	const getOutsideInstallStatus = (obj: any): string | undefined => {
		const value = obj?.[PROPERTY_KEYS.OUTSIDE_INSTALL_STATUS]
		return typeof value === 'string' ? value : undefined
	}

	/**
	 * Get cable pulled status from any object
	 * @param obj - Object containing account properties
	 * @returns Cable pulled boolean or undefined if not found
	 */
	const getCablePulled = (obj: any): boolean | undefined => {
		const value = obj?.[PROPERTY_KEYS.CABLE_PULLED]
		return typeof value === 'boolean' ? value : undefined
	}

	/**
	 * Get cable spliced status from any object
	 * @param obj - Object containing account properties
	 * @returns Cable spliced boolean or undefined if not found
	 */
	const getCableSpliced = (obj: any): boolean | undefined => {
		const value = obj?.[PROPERTY_KEYS.CABLE_SPLICED]
		return typeof value === 'boolean' ? value : undefined
	}

	/**
	 * Get cable terminated status from any object
	 * @param obj - Object containing account properties
	 * @returns Cable terminated boolean or undefined if not found
	 */
	const getCableTerminated = (obj: any): boolean | undefined => {
		const value = obj?.[PROPERTY_KEYS.CABLE_TERMINATED]
		return typeof value === 'boolean' ? value : undefined
	}

	/**
	 * Get templine status from any object
	 * @param obj - Object containing account properties
	 * @returns Templine status string or undefined if not found
	 */
	const getTemplineStatus = (obj: any): string | undefined => {
		const value = obj?.[PROPERTY_KEYS.TEMPLINE_STATUS]
		return typeof value === 'string' ? value : undefined
	}

	/**
	 * Get has equipment flag from any object
	 * @param obj - Object containing account properties
	 * @returns Has equipment boolean or undefined if not found
	 */
	const getHasEquipment = (obj: any): boolean | undefined => {
		const value = obj?.[PROPERTY_KEYS.HAS_EQUIPMENT]
		return typeof value === 'boolean' ? value : undefined
	}

	/**
	 * Get has tags flag from any object
	 * @param obj - Object containing account properties
	 * @returns Has tags boolean or undefined if not found
	 */
	const getHasTags = (obj: any): boolean | undefined => {
		const value = obj?.[PROPERTY_KEYS.HAS_TAGS]
		return typeof value === 'boolean' ? value : undefined
	}

	// ============================================================================
	// SETTERS - Validated property setters
	// ============================================================================

	/**
	 * Set account ID with validation
	 * @param obj - Object to set property on
	 * @param val - Account ID value
	 * @returns The modified object
	 */
	const setAccountId = (obj: any, val: number) => {
		if (typeof val !== 'number' || val <= 0) {
			console.warn('[useAccountSchema] Invalid accountId:', val)
			return obj
		}
		obj[PROPERTY_KEYS.ID] = val
		return obj
	}

	/**
	 * Set account status
	 * @param obj - Object to set property on
	 * @param val - Account status value
	 * @returns The modified object
	 */
	const setAccountStatus = (obj: any, val: string) => {
		if (typeof val !== 'string' || !val) {
			console.warn('[useAccountSchema] Invalid accountStatus:', val)
			return obj
		}
		obj[PROPERTY_KEYS.STATUS] = val
		return obj
	}

	/**
	 * Set zone ID with validation
	 * @param obj - Object to set property on
	 * @param val - Zone ID value
	 * @returns The modified object
	 */
	const setZoneId = (obj: any, val: number) => {
		if (typeof val !== 'number' || val <= 0) {
			console.warn('[useAccountSchema] Invalid zoneId:', val)
			return obj
		}
		obj[PROPERTY_KEYS.ZONE_ID] = val
		return obj
	}

	// ============================================================================
	// VALIDATORS - Property validation functions
	// ============================================================================

	/**
	 * Validate that a value is a valid account ID
	 * @param id - Value to validate
	 * @returns True if valid account ID
	 */
	const validateAccountId = (id: any): id is number => {
		return typeof id === 'number' && id > 0
	}

	/**
	 * Validate that a value is a valid zone ID
	 * @param id - Value to validate
	 * @returns True if valid zone ID
	 */
	const validateZoneId = (id: any): id is number => {
		return typeof id === 'number' && id > 0
	}

	/**
	 * Validate that a value is a valid account status
	 * @param status - Value to validate
	 * @returns True if valid status string
	 */
	const validateAccountStatus = (status: any): status is string => {
		return typeof status === 'string' && status.length > 0
	}

	// ============================================================================
	// BUILDERS - Property object builders
	// ============================================================================

	/**
	 * Build a complete properties object from account data
	 * This ensures all properties use the correct property keys
	 *
	 * @param data - Account data with known property names
	 * @returns Properties object with schema-compliant keys
	 */
	const buildProperties = (data: {
		accountId: number
		accountStatus: string
		zoneId: number
		siteSurveyStatus?: string
		insideInstallStatus?: string
		outsideInstallStatus?: string
		cablePulled?: boolean
		cableSpliced?: boolean
		cableTerminated?: boolean
		templineStatus?: string
		hasEquipment?: boolean
		hasTags?: boolean
	}) => ({
		[PROPERTY_KEYS.ID]: data.accountId,
		[PROPERTY_KEYS.STATUS]: data.accountStatus,
		[PROPERTY_KEYS.ZONE_ID]: data.zoneId,
		[PROPERTY_KEYS.SURVEY_STATUS]: data.siteSurveyStatus,
		[PROPERTY_KEYS.INSIDE_INSTALL_STATUS]: data.insideInstallStatus,
		[PROPERTY_KEYS.OUTSIDE_INSTALL_STATUS]: data.outsideInstallStatus,
		[PROPERTY_KEYS.CABLE_PULLED]: data.cablePulled,
		[PROPERTY_KEYS.CABLE_SPLICED]: data.cableSpliced,
		[PROPERTY_KEYS.CABLE_TERMINATED]: data.cableTerminated,
		[PROPERTY_KEYS.TEMPLINE_STATUS]: data.templineStatus,
		[PROPERTY_KEYS.HAS_EQUIPMENT]: data.hasEquipment,
		[PROPERTY_KEYS.HAS_TAGS]: data.hasTags
	})

	// ============================================================================
	// EXPORTS
	// ============================================================================

	return {
		// Constants
		PROPERTY_KEYS,

		// Getters
		getAccountId,
		getAccountStatus,
		getZoneId,
		getSurveyStatus,
		getInsideInstallStatus,
		getOutsideInstallStatus,
		getCablePulled,
		getCableSpliced,
		getCableTerminated,
		getTemplineStatus,
		getHasEquipment,
		getHasTags,

		// Setters
		setAccountId,
		setAccountStatus,
		setZoneId,

		// Validators
		validateAccountId,
		validateZoneId,
		validateAccountStatus,

		// Builders
		buildProperties
	}
}
